// @flow

import React, { type Element, useState, useEffect } from 'react'
import { VisibilityOff, Visibility } from '@material-ui/icons'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from '@material-ui/core'

type Props = {
  password: string,
  isPasswordValid: boolean,
  setIsValid: (boolean) => void,
}

const ErrorMessage = "Doesn't match"

function PasswordConfirmationField({
  password,
  isPasswordValid,
  setIsValid,
}: Props): Element<typeof React.Fragment> {
  const [isInteractedWith, setIsInteractedWith] = useState(false)
  const [currentValue, setCurrentValue] = useState('')
  const [isValid, setIsValidLocal] = useState(true)
  const [isShowPassword, setShowPassword] = useState(false)

  // Until this uses a core validation defined in the dr-bill-core project validate has to exist here.
  function validate(value: string) {
    if (!isInteractedWith) {
      return
    }

    // Do not validate the value until password is valid
    if (!isPasswordValid) {
      return
    }

    const isSuccess = value === password

    setIsValid(isSuccess)
    setIsValidLocal(isSuccess)
  }

  useEffect(() => {
    validate(currentValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password])

  const handleBlur = (event: SyntheticInputEvent<HTMLInputElement>): void => {
    const value = event.target.value

    validate(value)
  }

  const handleChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
    const value = event.target.value

    setIsInteractedWith(true)
    setCurrentValue(value)
    validate(value)
    setIsValidLocal(true)
  }

  const handleMouseDownPassword = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleClickShowPassword = () => {
    setShowPassword((currentValue) => !currentValue)
  }

  const errMsg = isValid ? '' : ErrorMessage

  return (
    <FormControl variant="outlined" fullWidth>
      <InputLabel variant="outlined" htmlFor="password-confirmation" required error={!isValid}>
        Confirm password
      </InputLabel>
      <OutlinedInput
        id="password-confirmation"
        type={isShowPassword ? 'text' : 'password'}
        value={currentValue}
        onChange={handleChange}
        onPaste={handleChange}
        onBlur={handleBlur}
        fullWidth
        required
        error={!isValid}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password confirmation visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {isShowPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        }
        label="Confirm password"
      />
      <FormHelperText error={!isValid}>{errMsg}</FormHelperText>
    </FormControl>
  )
}

export default PasswordConfirmationField
